import React from 'react';

import { ChangeHandler } from 'src/presentation/components/pages/integrations/types';
import CompactSection from 'src/presentation/components/pages/integrations/editorComponents/CompactSection';
import { Row } from 'src/presentation/components/forms/Layout';
import {
  MaskedField,
  WrappedField,
} from 'src/presentation/components/pages/integrations/editorComponents/EditorFields';
import Switch from 'src/presentation/components/forms/Switch';

export type KlaviyoConfigurationProps = {
  onChange: (attr: string) => ChangeHandler;
  maskedFields: string[];
};

const description = (
  <span>
    When turned on, the app will only update existing customers in your Klaviyo account.
    <br />
    IMPORTANT: If you have added the Klaviyo app before October 1, 2023, you must generate a new Private API key and
    replace the existing key in Gladly following{' '}
    <a
      target="_blank"
      rel="noopener noreferrer"
      href="https://help.gladly.com/docs/set-up-klaviyo#add-klaviyo-to-gladly"
    >
      these instructions
    </a>
    .
  </span>
);

const KlaviyoConfiguration = ({ onChange, maskedFields }: KlaviyoConfigurationProps): JSX.Element => {
  return (
    <>
      <CompactSection
        description={`The Gladly-Klaviyo integration enables you to segment customers in Klaviyo based on whether the customer is engaged in a support conversation in Gladly.`}
      >
        <Row>
          <WrappedField
            attr="name"
            label="Name"
            placeholder="App Name"
            description="This is the name that will be displayed throughout Gladly"
            data-aid="klaviyo-integration-name"
            autoFocus={true}
            onChange={onChange('name')}
          />
        </Row>
        <Row>
          <MaskedField
            attr="secrets.privateApiKey"
            showClearButton={maskedFields.includes('privateApiKey')}
            isMasked={maskedFields.includes('privateApiKey')}
            label="Klaviyo Private API Key"
            data-aid="klaviyo-private-key"
            description="Used to authenticate API requests to Klaviyo. You can obtain and make changes to your API keys from your Klaviyo account's Settings page under the API Keys tab."
            onChange={onChange('secrets.privateApiKey')}
          />
        </Row>
        <Row>
          <Switch
            attr="connectionSettings.doNotCreateNewKlaviyoCustomers"
            data-aid="klaviyo-do-not-create-new-customers-toggle"
            label="Do Not Create New Klaviyo Customers"
            description={description}
            useLocalControlState={true}
          />
        </Row>
      </CompactSection>
    </>
  );
};

export default KlaviyoConfiguration;
